// Enhanced ChatInterface component with dynamic expert-based template support
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Alert,
  CircularProgress,
  Skeleton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ButtonGroup,
  IconButton,
  Fade,
  Badge
} from '@mui/material';
import {
  FlashOn as FlashIcon,
  Psychology as AiIcon,
  Cached as CacheIcon,
  TrendingUp as RevenueIcon,
  ShoppingCart as ProductIcon,
  Campaign as MarketingIcon,
  Search as SearchIcon,
  FilterAlt as FunnelIcon,
  AccessTime as TimeIcon,
  Dashboard as DashboardIcon,
  ExpandMore,
  ExpandLess,
  Refresh,
  Settings as SettingsIcon,
  Restaurant as RecipeIcon
} from '@mui/icons-material';

// Helper function to get appropriate icon for template
const getTemplateIcon = (templateName) => {
  const name = templateName.toLowerCase();
  if (name.includes('revenue') || name.includes('sales')) return <RevenueIcon />;
  if (name.includes('product')) return <ProductIcon />;
  if (name.includes('channel') || name.includes('marketing')) return <MarketingIcon />;
  if (name.includes('search') || name.includes('keyword')) return <SearchIcon />;
  if (name.includes('funnel') || name.includes('conversion')) return <FunnelIcon />;
  if (name.includes('dashboard') || name.includes('comprehensive')) return <DashboardIcon />;
  return <FlashIcon />; // Default icon
};

const TemplateQuickActions = ({ onSelectTemplate, disabled, selectedExpertId, onCustomizeTemplate }) => {
  const [expertTemplates, setExpertTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  console.log('[TemplateQuickActions] 🎭 Render - selectedExpertId:', selectedExpertId);
  console.log('[TemplateQuickActions] 🎭 Current templates count:', expertTemplates.length);

  // Load expert templates when expert changes
  useEffect(() => {
    console.log('[TemplateQuickActions] 🔄 useEffect triggered - selectedExpertId:', selectedExpertId);
    if (selectedExpertId) {
      console.log('[TemplateQuickActions] ✅ Expert ID present, loading templates...');
      loadExpertTemplates();
    } else {
      console.log('[TemplateQuickActions] ⚠️  No expert ID, clearing templates');
      setExpertTemplates([]);
    }
  }, [selectedExpertId]);

  const loadExpertTemplates = async () => {
    console.log('[TemplateQuickActions] 🚀 loadExpertTemplates called for expert:', selectedExpertId);
    setLoading(true);
    setError(null);
    
    try {
      const url = `/api/chat/experts/${selectedExpertId}/quick-templates`;
      console.log('[TemplateQuickActions] 📤 Fetching:', url);
      const response = await axios.get(url);
      console.log('[TemplateQuickActions] 📥 Response:', response.data);
      
      if (response.data.success) {
        const templates = response.data.templates || [];
        console.log('[TemplateQuickActions] ✅ Loaded', templates.length, 'quick report templates');
        setExpertTemplates(templates);
        
        // Log each template for debugging
        templates.forEach((t, i) => {
          console.log(`[TemplateQuickActions]    ${i + 1}. ${t.display_label || t.name} (ID: ${t.id})`);
        });
      } else {
        console.error('[TemplateQuickActions] ❌ Response not successful:', response.data);
        setError('Failed to load expert templates');
      }
      
    } catch (error) {
      console.error('[TemplateQuickActions] ❌ Failed to load expert templates:', error);
      console.error('[TemplateQuickActions] ❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError(error.response?.data?.message || error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const executeTemplate = async (template) => {
    try {
      // Debug: Log template data
      console.log('[TemplateQuickActions] 🚀 Executing template:', template);
      console.log('[TemplateQuickActions] 🎯 Template ID:', template.id);
      console.log('[TemplateQuickActions] 🎯 Template Name:', template.name);
      
      // Execute template directly with default parameters
      const defaultParams = {};
      
      // Extract default parameters from template
      if (template.resolved_parameters) {
        try {
          const paramSchema = JSON.parse(template.resolved_parameters);
          console.log('[TemplateQuickActions] 📋 Parameter schema:', paramSchema);
          
          for (const [key, param] of Object.entries(paramSchema)) {
            if (param.default !== undefined) {
              defaultParams[key] = param.default;
              console.log(`[TemplateQuickActions]    ✅ ${key} = ${param.default}`);
            } else if (param.type === 'date') {
              // Auto-generate default dates for date parameters
              if (key.includes('start') || key.includes('from')) {
                defaultParams[key] = 'today-30';
                console.log(`[TemplateQuickActions]    ✅ ${key} = today-30 (auto-generated)`);
              } else if (key.includes('end') || key.includes('to')) {
                defaultParams[key] = 'today';
                console.log(`[TemplateQuickActions]    ✅ ${key} = today (auto-generated)`);
              }
            }
          }
          
          console.log('[TemplateQuickActions] 📦 Final parameters to send:', defaultParams);
        } catch (parseError) {
          console.error('[TemplateQuickActions] ❌ Could not parse template parameters:', parseError);
        }
      }
      
      // Validate template ID before execution
      if (!template.id) {
        console.error('Template ID is missing!', template);
        alert('Error: Template ID is missing. Please refresh the page and try again.');
        return;
      }
      
      // Call the callback with template execution data
      const templateData = {
        type: 'template_execution',
        templateId: template.id,
        templateName: template.display_label || template.name,
        parameters: defaultParams,
        isQuickReport: true
      };
      
      console.log('Calling onSelectTemplate with:', templateData);
      await onSelectTemplate(templateData);
      
    } catch (error) {
      console.error('Failed to execute template:', error);
    }
  };

  // If no expert selected, show selection prompt
  if (!selectedExpertId) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Select an expert to see personalized Quick Reports
        </Typography>
      </Alert>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
          Loading Quick Reports...
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} variant="rectangular" width={120} height={36} />
          ))}
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          {error}
        </Typography>
        <Button size="small" onClick={loadExpertTemplates} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  // No templates available
  if (expertTemplates.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          No Quick Reports configured for this expert yet.
        </Typography>
      </Alert>
    );
  }

  // Helper to format parameter values for display
  const formatParamValue = (param) => {
    if (param.default === undefined) return param.type === 'date' ? 'today' : '...';
    
    // Format dates specially - they're critical for data analysts!
    if (param.type === 'date') {
      const value = String(param.default);
      if (value.includes('today')) {
        return value.replace('today', 'Today').replace('-', ' - ');
      }
      return value;
    }
    
    return param.default;
  };

  // Helper to get parameter count and preview
  const getParameterInfo = (template) => {
    if (!template.resolved_parameters) return { count: 0, preview: '', fullDetails: [] };
    
    try {
      const paramSchema = JSON.parse(template.resolved_parameters);
      
      // Filter out technical parameters (database, table names, etc.)
      const technicalParams = ['database', 'table', 'table_name', 'database_name', 'schema'];
      const userParams = Object.entries(paramSchema).filter(
        ([key]) => !technicalParams.includes(key.toLowerCase())
      );
      
      const count = userParams.length;
      
      // Create abbreviated preview for button display (dates first!)
      const dateParams = userParams.filter(([_, param]) => param.type === 'date');
      const otherParams = userParams.filter(([_, param]) => param.type !== 'date');
      const sortedParams = [...dateParams, ...otherParams];
      
      const preview = sortedParams
        .slice(0, 2)
        .map(([key, param]) => `${key}: ${formatParamValue(param)}`)
        .join(', ');
      
      // Create full details for tooltip (ALL parameters with proper formatting)
      const fullDetails = sortedParams.map(([key, param]) => ({
        name: key,
        description: param.description || key,
        value: formatParamValue(param),
        type: param.type
      }));
      
      return { 
        count, 
        preview: count > 2 ? `${preview}...` : preview,
        fullDetails
      };
    } catch (e) {
      return { count: 0, preview: '', fullDetails: [] };
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
        Quick Reports for Expert (personalized templates)
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {expertTemplates.map(template => {
          const paramInfo = getParameterInfo(template);
          
          return (
            <Tooltip 
              key={template.id}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 450,
                    bgcolor: 'grey.900',
                    '& .MuiTooltip-arrow': {
                      color: 'grey.900',
                    },
                  }
                }
              }}
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {template.description || template.name}
                  </Typography>
                  
                  {/* Show ALL parameters with their defaults */}
                  {paramInfo.fullDetails && paramInfo.fullDetails.length > 0 && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.light', display: 'block', mb: 0.5 }}>
                        📊 Analysis Parameters:
                      </Typography>
                      {paramInfo.fullDetails.map((detail, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'baseline',
                            gap: 1,
                            mb: 0.5,
                            pl: 1,
                            borderLeft: detail.type === 'date' ? '3px solid' : '2px solid',
                            borderLeftColor: detail.type === 'date' ? 'warning.main' : 'grey.700'
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontFamily: 'monospace',
                              color: detail.type === 'date' ? 'warning.light' : 'primary.light',
                              fontWeight: detail.type === 'date' ? 'bold' : 'normal',
                              minWidth: '100px'
                            }}
                          >
                            {detail.type === 'date' ? '📅 ' : ''}{detail.name}:
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontFamily: 'monospace',
                              color: detail.type === 'date' ? 'warning.main' : 'success.light',
                              fontWeight: detail.type === 'date' ? 'bold' : 'normal',
                              flex: 1
                            }}
                          >
                            {detail.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  {template.usage_count > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
                      📈 Used {template.usage_count} times • ⚡ Avg: {template.avg_execution_time}ms
                    </Typography>
                  )}
                  
                  <Typography variant="caption" display="block" sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'grey.700', color: 'info.light' }}>
                    💡 Click button for instant results with these defaults<br/>
                    ⚙️ Click gear icon to customize parameters
                  </Typography>
                </Box>
              }
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'stretch',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'success.main',
                  '&:hover .gear-button': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                {/* Main Button - Execute with defaults */}
                <Button
                  size="small"
                  variant="text"
                  startIcon={getTemplateIcon(template.name)}
                  onClick={() => executeTemplate(template)}
                  disabled={disabled}
                  sx={{
                    borderRadius: 0,
                    textTransform: 'none',
                    color: 'success.main',
                    paddingRight: 1,
                    borderRight: '1px solid',
                    borderRightColor: 'success.main',
                    '&:hover': {
                      backgroundColor: 'success.light',
                      color: 'white'
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                      {template.display_label || template.name}
                    </Typography>
                    {paramInfo.preview && (
                      <Typography 
                        variant="caption" 
                        display="block" 
                        sx={{ 
                          fontSize: '9px',
                          opacity: 0.7,
                          fontFamily: 'monospace',
                          maxWidth: '200px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        ↳ {paramInfo.preview}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Usage count badge */}
                  {template.usage_count > 0 && (
                    <Chip 
                      label={template.usage_count} 
                      size="small" 
                      sx={{ 
                        ml: 0.5, 
                        height: 16, 
                        fontSize: '10px',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        color: 'success.main'
                      }}
                    />
                  )}
                </Button>
                
                {/* Gear Button - Customize parameters */}
                <IconButton
                  className="gear-button"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onCustomizeTemplate) {
                      onCustomizeTemplate(template);
                    }
                  }}
                  disabled={disabled}
                  sx={{
                    borderRadius: 0,
                    width: '32px',
                    height: '100%',
                    color: 'success.main',
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <Badge 
                    badgeContent={paramInfo.count || null} 
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '9px',
                        height: '14px',
                        minWidth: '14px',
                        padding: '0 3px'
                      }
                    }}
                  >
                    <SettingsIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
        These are your most-used templates - they respond in under 2 seconds
      </Typography>
    </Box>
  );
};

const TemplateParameterControls = ({ 
  template, 
  currentParameters, 
  onParameterChange, 
  loading 
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  if (!template || !template.parameters) return null;
  
  let paramSchema = {};
  let uiConfig = {};
  
  try {
    paramSchema = JSON.parse(template.parameters || '{}');
    uiConfig = JSON.parse(template.ui_config || '{}');
  } catch (error) {
    console.warn('Could not parse template configuration:', error);
    return null;
  }
  
  const primaryParams = uiConfig.parameter_groups?.primary || [];
  const advancedParams = uiConfig.parameter_groups?.advanced || [];

  const renderParameterControl = (paramName, paramDef) => {
    if (!paramDef) return null;
    
    const uiConf = paramDef.ui_config || {};
    const currentValue = currentParameters[paramName] || paramDef.default;

    switch (uiConf.control_type) {
      case 'button_group':
        return (
          <ButtonGroup key={paramName} size="small" variant="outlined">
            {(paramDef.options || []).map(option => (
              <Button
                key={option.value}
                variant={currentValue === option.value ? 'contained' : 'outlined'}
                onClick={() => onParameterChange(paramName, option.value)}
                disabled={loading}
                sx={{ textTransform: 'capitalize' }}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        );
        
      case 'select':
      default:
        if (!paramDef.options || paramDef.options.length === 0) return null;
        
        return (
          <FormControl key={paramName} size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{uiConf.display_name || paramName}</InputLabel>
            <Select
              value={currentValue || ''}
              label={uiConf.display_name || paramName}
              onChange={(e) => onParameterChange(paramName, e.target.value)}
              disabled={loading}
            >
              {paramDef.options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
    }
  };

  // If no UI-enabled parameters, don't show controls
  const hasUiParameters = [...primaryParams, ...advancedParams].some(paramName => {
    const paramDef = paramSchema[paramName];
    return paramDef && paramDef.options && paramDef.options.length > 0;
  });

  if (!hasUiParameters) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Primary Controls - Always Visible */}
      {primaryParams.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
          {primaryParams.map(paramName => 
            renderParameterControl(paramName, paramSchema[paramName])
          )}
          
          {advancedParams.length > 0 && (
            <IconButton 
              size="small" 
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
      )}

      {/* Advanced Controls - Collapsible */}
      {advancedParams.length > 0 && (
        <Collapse in={showAdvanced}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            alignItems: 'center'
          }}>
            {advancedParams.map(paramName => 
              renderParameterControl(paramName, paramSchema[paramName])
            )}
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={() => {
                // Reset to defaults
                const defaults = {};
                for (const [key, param] of Object.entries(paramSchema)) {
                  if (param.default !== undefined) {
                    defaults[key] = param.default;
                  }
                }
                onParameterChange('reset', defaults);
              }}
              disabled={loading}
            >
              Reset
            </Button>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const TemplateResultCard = ({ result, onParameterChange }) => {
  const [parameters, setParameters] = useState(result.parameters_used || {});
  const [loading, setLoading] = useState(false);

  const handleParameterChange = async (paramName, value) => {
    if (paramName === 'reset') {
      setParameters(value);
    } else {
      setParameters(prev => ({ ...prev, [paramName]: value }));
    }
    
    setLoading(true);
    try {
      // Re-execute template with new parameters
      await onParameterChange(result.template.id, parameters);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Header with Template Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getTemplateIcon(result.template.name)}
            <Typography variant="h6">{result.template.name}</Typography>
            <Chip label="Template" size="small" color="success" />
            {result.execution_time && (
              <Chip 
                label={`${result.execution_time}ms`} 
                size="small" 
                color="info"
                icon={<TimeIcon />}
              />
            )}
          </Box>
        </Box>

        {/* Embedded Parameter Controls */}
        <TemplateParameterControls
          template={result.template}
          currentParameters={parameters}
          onParameterChange={handleParameterChange}
          loading={loading}
        />

        {/* Results Display with Loading Overlay */}
        <Box sx={{ position: 'relative', minHeight: 200 }}>
          {loading && (
            <Box sx={{ 
              position: 'absolute', 
              inset: 0, 
              bgcolor: 'rgba(255,255,255,0.8)', 
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CircularProgress />
            </Box>
          )}
          
          <Fade in={!loading} timeout={300}>
            <Box>
              {/* Template results would be rendered here */}
              <Typography variant="body2" color="textSecondary">
                Template results will be displayed here
              </Typography>
            </Box>
          </Fade>
        </Box>
      </CardContent>
    </Card>
  );
};

const MessagePerformanceIndicator = ({ performance }) => {
  if (!performance) return null;

  const isTemplate = performance.templateUsed || performance.source === 'template';
  const isCached = performance.fromCache || performance.cached;
  const executionTime = performance.executionTime || performance.queryTime || 0;

  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
      {isTemplate && (
        <Tooltip title={`Template: ${performance.templateUsed || 'Quick Report'}`}>
          <Chip
            icon={<FlashIcon />}
            label="Template"
            size="small"
            color="success"
            variant="outlined"
          />
        </Tooltip>
      )}
      
      {isCached && (
        <Tooltip title="Results from cache (instant)">
          <Chip
            icon={<CacheIcon />}
            label="Cached"
            size="small"
            color="info"
            variant="outlined"
          />
        </Tooltip>
      )}
      
      {!isTemplate && (
        <Tooltip title="AI-powered analysis">
          <Chip
            icon={<AiIcon />}
            label="Claude AI"
            size="small"
            color="warning"
            variant="outlined"
          />
        </Tooltip>
      )}
      
      {executionTime > 0 && (
        <Tooltip title="Query execution time">
          <Chip
            icon={<TimeIcon />}
            label={`${executionTime}ms`}
            size="small"
            variant="outlined"
            color={executionTime < 1000 ? 'success' : executionTime < 3000 ? 'warning' : 'error'}
          />
        </Tooltip>
      )}

      {performance.confidence && (
        <Tooltip title="Template match confidence">
          <Chip
            label={`${Math.round(performance.confidence * 100)}% match`}
            size="small"
            variant="outlined"
            color={performance.confidence > 0.7 ? 'success' : performance.confidence > 0.4 ? 'warning' : 'error'}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const ResearchProgressBar = ({ progress }) => {
  if (!progress) return null;

  const percentage = (progress.step / progress.total) * 100;
  const isTemplate = progress.currentAction?.includes('template') || 
                     progress.currentAction?.includes('cache');

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {isTemplate ? <FlashIcon color="success" /> : <AiIcon color="primary" />}
          <Typography variant="body2" sx={{ ml: 1 }}>
            {progress.currentAction || 'Processing...'}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={percentage}
          color={isTemplate ? 'success' : 'primary'}
        />
        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
          Step {progress.step} of {progress.total}
        </Typography>
      </CardContent>
    </Card>
  );
};

const TemplateSystemStatus = ({ stats }) => {
  if (!stats) return null;

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        Expert Template System Active
      </Typography>
      <Typography variant="caption" display="block">
        • {stats.templatesAvailable || 0} personalized templates available
        • {stats.cacheHitRate || 0}% cache hit rate today
        • Avg response: {stats.avgResponseTime || '<1s'}
      </Typography>
    </Alert>
  );
};

// Export components for use in main ChatInterface
export {
  TemplateQuickActions,
  TemplateParameterControls,
  TemplateResultCard,
  MessagePerformanceIndicator,
  ResearchProgressBar,
  TemplateSystemStatus
};